import { create } from 'zustand';

import { MemberStatus } from '@/modules/team/types/team-member';

import { MemberAccessRow } from '../types/member-access';

/**
 * A user's in-progress edit. `groupId` is kept as a string for select compatibility.
 * `selected` tracks invite-mode selection — keyed by userId so it survives filtering.
 */
export interface MemberEdit {
  groupId: string | null;
  email: string | null;
  selected: boolean;
}

/**
 * The pristine server value for a member, used to diff edits.
 * `name` is retained so the invite-failures dialog can resolve names without a refetch.
 */
export interface MemberBaseline {
  groupId: string | null;
  email: string | null;
  status: MemberStatus;
  name: string;
}

interface MemberEditsState {
  edits: Record<number, MemberEdit>;
  baseline: Record<number, MemberBaseline>;
  /**
   * Bumped whenever edits are reset/cleared/rebaselined wholesale. Editable cells key
   * on it so controlled inputs (Radix Select / native Input) remount to the new value
   * — a guaranteed visual reset that doesn't depend on each control honoring an
   * external controlled-value change.
   */
  version: number;
  /**
   * Reconcile server rows into the store: server-authoritative for unedited rows, never
   * clobbers an in-progress local edit, identity-stable on no-op.
   */
  hydrate: (rows: MemberAccessRow[]) => void;
  /** Overwrite baseline + edits for the given rows (post-mutation resync). */
  rebaseline: (rows: MemberAccessRow[]) => void;
  setGroupId: (userId: number, groupId: string | null) => void;
  setEmail: (userId: number, email: string | null) => void;
  setSelected: (userId: number, selected: boolean) => void;
  setSelectedMany: (userIds: number[], selected: boolean) => void;
  /** Idempotent: returns the same state ref when nothing changes (Trap B defense). */
  deselectUsers: (userIds: number[]) => void;
  /** "Reset values": edits := a fresh working clone of baseline. */
  resetAllEdits: () => void;
  /** Wipe everything (on mount/unmount + after a successful mutation). */
  clearAll: () => void;
}

/**
 * Edits cache for the member-access table, keyed by userId and decoupled from the
 * visible/loaded window. This is what makes filtering non-destructive: an edit or
 * selection to a row that is later filtered out of view persists here and reappears
 * when the row becomes visible again. It is also the single live source for the
 * footer counts, so pagination cannot reset change-tracking (OPS-1500).
 */
export const useMemberEditsStore = create<MemberEditsState>(set => ({
  edits: {},
  baseline: {},
  version: 0,

  // Reconcile server rows into the store. Server-authoritative for UNEDITED rows, but
  // never clobbers an in-progress local edit (OPS-1500):
  //  - new row            → seed baseline + edit from the server.
  //  - unedited row       → adopt the latest server value (so a revisit/refetch shows
  //                         fresh data even after a background refetch serves stale first).
  //  - locally-edited row → keep the user's edit; baseline still tracks server truth so
  //                         the change-count diff stays correct.
  // Returns the same state object when nothing changed (identity-stable no-op → no
  // re-render churn from the new query object produced on every fetch).
  hydrate: rows =>
    set(state => {
      let nextEdits = state.edits;
      let nextBaseline = state.baseline;
      let editsChanged = false;
      let baselineChanged = false;

      const cloneEdits = () => {
        if (!editsChanged) {
          nextEdits = { ...state.edits };
          editsChanged = true;
        }
      };
      const cloneBaseline = () => {
        if (!baselineChanged) {
          nextBaseline = { ...state.baseline };
          baselineChanged = true;
        }
      };

      for (const row of rows) {
        const prevBaseline = state.baseline[row.userId];
        const prevEdit = state.edits[row.userId];

        // Whether the user has a pending local edit (edit diverged from the old baseline).
        const isLocallyEdited =
          !!prevBaseline &&
          !!prevEdit &&
          (prevEdit.groupId !== prevBaseline.groupId || prevEdit.email !== prevBaseline.email);

        // Baseline always tracks server truth.
        const baselineDiffers =
          !prevBaseline ||
          prevBaseline.groupId !== row.groupId ||
          prevBaseline.email !== row.email ||
          prevBaseline.status !== row.status ||
          prevBaseline.name !== row.name;
        if (baselineDiffers) {
          cloneBaseline();
          nextBaseline[row.userId] = {
            groupId: row.groupId,
            email: row.email,
            status: row.status,
            name: row.name,
          };
        }

        // Edit adopts the server value unless the user has edited this row locally.
        if (!isLocallyEdited) {
          const desired = {
            groupId: row.groupId,
            email: row.email,
            selected: prevEdit?.selected ?? false,
          };
          if (
            !prevEdit ||
            prevEdit.groupId !== desired.groupId ||
            prevEdit.email !== desired.email
          ) {
            cloneEdits();
            nextEdits[row.userId] = desired;
          }
        }
      }

      if (!editsChanged && !baselineChanged) return state;
      return { edits: nextEdits, baseline: nextBaseline };
    }),

  // OVERWRITE for the given userIds (post-save/invite resync so changes return to 0).
  rebaseline: rows =>
    set(state => {
      const nextBaseline = { ...state.baseline };
      const nextEdits = { ...state.edits };
      for (const row of rows) {
        nextBaseline[row.userId] = {
          groupId: row.groupId,
          email: row.email,
          status: row.status,
          name: row.name,
        };
        nextEdits[row.userId] = { groupId: row.groupId, email: row.email, selected: false };
      }
      return { edits: nextEdits, baseline: nextBaseline, version: state.version + 1 };
    }),

  setGroupId: (userId, groupId) =>
    set(state => {
      const prev = state.edits[userId];
      if (!prev || prev.groupId === groupId) return state;
      return { edits: { ...state.edits, [userId]: { ...prev, groupId } } };
    }),

  setEmail: (userId, email) =>
    set(state => {
      const prev = state.edits[userId];
      if (!prev || prev.email === email) return state;
      return { edits: { ...state.edits, [userId]: { ...prev, email } } };
    }),

  setSelected: (userId, selected) =>
    set(state => {
      const prev = state.edits[userId];
      if (!prev || prev.selected === selected) return state;
      return { edits: { ...state.edits, [userId]: { ...prev, selected } } };
    }),

  setSelectedMany: (userIds, selected) =>
    set(state => {
      const next = { ...state.edits };
      let changed = false;
      for (const userId of userIds) {
        const prev = next[userId];
        if (prev && prev.selected !== selected) {
          next[userId] = { ...prev, selected };
          changed = true;
        }
      }
      return changed ? { edits: next } : state;
    }),

  deselectUsers: userIds =>
    set(state => {
      const next = { ...state.edits };
      let changed = false;
      for (const userId of userIds) {
        const prev = next[userId];
        if (prev && prev.selected) {
          next[userId] = { ...prev, selected: false };
          changed = true;
        }
      }
      return changed ? { edits: next } : state;
    }),

  resetAllEdits: () =>
    set(state => {
      const next: Record<number, MemberEdit> = {};
      for (const [id, base] of Object.entries(state.baseline)) {
        next[Number(id)] = { groupId: base.groupId, email: base.email, selected: false };
      }
      return { edits: next, version: state.version + 1 };
    }),

  clearAll: () => set(state => ({ edits: {}, baseline: {}, version: state.version + 1 })),
}));

/**
 * Counts members whose group differs from the server baseline.
 * Predicate MUST match `mapMemberAccessToUpdateRequest` exactly so the footer count
 * and the persisted diff never disagree.
 */
export const countGroupChanges = (
  edits: Record<number, MemberEdit>,
  baseline: Record<number, MemberBaseline>
): number => {
  let count = 0;
  for (const [id, edit] of Object.entries(edits)) {
    const base = baseline[Number(id)];
    if (base && base.groupId !== edit.groupId && edit.groupId !== null) count++;
  }
  return count;
};

/** Counts currently-selected members (across all filters). */
export const countSelected = (edits: Record<number, MemberEdit>): number => {
  let count = 0;
  for (const edit of Object.values(edits)) {
    if (edit.selected) count++;
  }
  return count;
};
